import { useEffect, useState, useRef } from "react";
import { Switch, Route, Redirect, useLocation, Router as WouterRouter } from 'wouter';

import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { TourButton, TourProvider } from "@/components/ProductTour";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { PendingQuoteNotice } from "@/components/pending-quote-notice";
import { useAuth } from "@/hooks/use-auth";
import { Skeleton } from "@/components/ui/skeleton";
import { FeedbackWidget } from "@/components/feedback-widget";
import { HelpAgent } from "@/components/help-agent";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Lock, CheckCircle2, Tag, Building2, Wrench, Menu, X as XIcon, Monitor, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { getTryAQuoteDestination } from "@/lib/signup-flow";
import { useToast } from "@/hooks/use-toast";

import LandingPage from "@/pages/landing";
import LoginPage from "@/pages/login";
import SignupPage from "@/pages/signup";
import ResetPasswordPage from "@/pages/reset-password";
import DashboardPage from "@/pages/dashboard";
import QuotesPage from "@/pages/quotes";
import QuoteNewPage from "@/pages/quote-new";
import QuoteAIPage from "@/pages/quote-ai";
import QuoteWelcomePage from "@/pages/quote-welcome";
import QuoteTemplatePage from "@/pages/quote-template";
import QuoteManualPage from "@/pages/quote-manual";
import QuoteSurveyPage from "@/pages/quote-survey";
import SiteSurveysPage from "@/pages/site-surveys";
import QuoteDetailPage from "@/pages/quote-detail";
import QuoteEditPage from "@/pages/quote-edit";
import ClientsPage from "@/pages/clients";
import CostLibraryPage from "@/pages/cost-library";
import AdjustmentsPage from "@/pages/adjustments";
import SettingsPage from "@/pages/settings";
import QuoteDocumentDesignPage from "@/pages/quote-document-design";
import { PdfTemplateWarningListener } from "@/components/pdf-template-warning-listener";
import PricingIntelligencePage from "@/pages/pricing-intelligence";
import ReferralsPage from "@/pages/referrals";
import AdminWithdrawalsPage from "@/pages/admin-withdrawals";
import AdminUsersPage from "@/pages/admin-users";
import AdminThemePage from "@/pages/admin-theme";
import AdminInboxPage from "@/pages/admin-inbox";
import AdminMaintenancePage from "@/pages/admin-maintenance";
import AdminPartnerReferralsPage from "@/pages/admin-partner-referrals";
import AdminAccountDeletionsPage from "@/pages/admin-account-deletions";
import AdminGeneratedQuotesPage from "@/pages/admin-generated-quotes";
import AdminVocabularyGapsPage from "@/pages/admin-vocabulary-gaps";
import AdminCostLibraryPage from "@/pages/admin-cost-library";
import AdminPriceReviewPage from "@/pages/admin-price-review";
import AdminQuotesPage from "@/pages/admin-quotes";
import AdminUpdatesPage from "@/pages/admin-updates";
import StructSpectrumPage from "@/pages/structspectrum";
import TermsPage from "@/pages/terms";
import PrivacyPage from "@/pages/privacy";
import CookiesPage from "@/pages/cookies";
import ReferralTermsPage from "@/pages/referral-terms";
import BlogIndexPage from "@/pages/blog-index";
import ComparisonsIndexPage from "@/pages/comparisons-index";
import BlogPostPage from "@/pages/blog-post";
import FaqPage from "@/pages/faq-page";
import FaqIndexPage from "@/pages/faq-index";
import AlternativePage from "@/pages/alternative-page";
import ThankYouPage from "@/pages/thank-you";
import SlugLandingPage from "@/pages/slug-landing";
import NotFound from "@/pages/not-found";
import PublicQuotePage from "@/pages/public-quote";
import PublicGuestClaimPage from "@/pages/public-guest-claim";
import QuotingSoftwareForBuildersPage from "@/pages/quoting-software-for-builders";
import ConstructionEstimatingSoftwarePage from "@/pages/construction-estimating-software";
import BestQuotingSoftwarePage from "@/pages/best-quoting-software";
import BestQuotingSoftwareForUKBuildersPage from "@/pages/best-quoting-software-for-uk-builders";
import BuildxactAlternativePage from "@/pages/buildxact-alternative";
import EstimatorXpressAlternativePage from "@/pages/estimatorxpress-alternative";
import SimproAlternativePage from "@/pages/simpro-alternative";
import QuotingAppForBuildersPage from "@/pages/quoting-app-for-builders";
import QuotingSoftwareForContractorsPage from "@/pages/quoting-software-for-contractors";
import TradesmanQuotingToolPage from "@/pages/tradesman-quoting-tool";
import JobQuotingSoftwarePage from "@/pages/job-quoting-software";
import EstimatingSoftwareForBuildersPage from "@/pages/estimating-software-for-builders";
import TradifyAlternativePage from "@/pages/tradify-alternative";
import FergusAlternativePage from "@/pages/fergus-alternative";
import EstimageAlternativePage from "@/pages/estimage-alternative";
import PricejobAlternativePage from "@/pages/pricejob-alternative";
import BuildernAlternativePage from "@/pages/buildern-alternative";
import CubitAlternativePage from "@/pages/cubit-alternative";
import EvaluateAlternativePage from "@/pages/evaluate-alternative";
import QuintadenaAlternativePage from "@/pages/quintadena-alternative";
import EasyestimateAlternativePage from "@/pages/easyestimate-alternative";
import AiQuotingSoftwarePage from "@/pages/ai-quoting-software";
import AutoQuotePage from "@/pages/auto-quote";
import QuoteFromDrawingsPage from "@/pages/quote-from-drawings";
import QuoteFromDescriptionPage from "@/pages/quote-from-description";
import QuoteFromPhotoPage from "@/pages/quote-from-photo";
import AiTakeoffSoftwarePage from "@/pages/ai-takeoff-software";
import AiConstructionEstimatingSoftwarePage from "@/pages/ai-construction-estimating-software";
import AboutPage from "@/pages/about";
import ForSmallBuildersPage from "@/pages/for-small-builders";
import ForSoleTradersPage from "@/pages/for-sole-traders";
import GuideQuotingForUKBuildersPage from "@/pages/guide-quoting-for-uk-builders";
import GuideRunningABuildingBusinessUKPage from "@/pages/guide-running-a-building-business-uk";
import GuidesIndexPage from "@/pages/guides-index";
import { ElectriciansPage, PlumbersPage, RoofersPage, CarpentersPage, PlasterersPage, DecoratorsPage } from "@/pages/trade-vertical-page";
import GlossaryPage from "@/pages/glossary";
import UKBuilderQuotingSurvey2026Page from "@/pages/uk-builder-quoting-survey-2026";

const PLAN_FEATURES: Record<string, string[]> = {
  starter: [
    "10 Auto quotes per month",
    "Unlimited Template & Manual quotes",
    "Professional PDF export",
    "Cost Library with 31 categories",
    "Email support",
  ],
  pro: [
    "25 Auto quotes per month",
    "Unlimited Template & Manual quotes",
    "Pricing Intelligence system",
    "Expert Review Feature",
    "Regional UK pricing",
    "Priority support",
  ],
  team: [
    "50 Auto quotes per month",
    "Unlimited Template & Manual quotes",
    "Everything in Starter",
    "Learned Adjustments",
    "Drawing analysis",
    "Dedicated support",
  ],
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  starter: "Perfect for sole traders getting started.",
  pro: "For growing businesses wanting more power.",
  team: "For teams who need collaboration.",
};

function TrialExpiredPaywall() {
  const { toast } = useToast();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [codeApplied, setCodeApplied] = useState(false);

  const { data: plans, isLoading: plansLoading } = useQuery<any[]>({ queryKey: ["/api/stripe/plans"] });

  const applyCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/referrals/apply", { code: code.trim().toLowerCase() });
      return res.json();
    },
    onSuccess: () => {
      setCodeApplied(true);
      toast({ title: "Referral code applied", description: "Your referrer will earn a reward when you subscribe." });
    },
    onError: (error: any) => {
      let msg = "Invalid or already used referral code.";
      try {
        const raw = error?.message || "";
        const jsonPart = raw.substring(raw.indexOf("{"));
        msg = JSON.parse(jsonPart)?.message || msg;
      } catch {}
      toast({ title: "Could not apply code", description: msg, variant: "destructive" });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const res = await apiRequest("POST", "/api/stripe/create-checkout", {
        priceId,
        source: "trial_paywall",
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url;
      setCheckoutLoading(null);
    },
    onError: () => {
      setCheckoutLoading(null);
      toast({ title: "Error", description: "Could not start checkout. Please try again.", variant: "destructive" });
    },
  });

  const handleSubscribe = (tier: string) => {
    const plan = plans?.find((p: any) => p.tier === tier);
    if (plan?.priceId) {
      setCheckoutLoading(tier);
      checkoutMutation.mutate(plan.priceId);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-full px-4 py-12 text-center" data-testid="paywall-trial-expired">
      <div className="mb-6 flex items-center justify-center w-16 h-16 rounded-full bg-muted">
        <Lock className="h-8 w-8 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Your free trial has ended</h1>
      <p className="text-muted-foreground mb-6 max-w-sm">
        Subscribe to continue generating AI-powered construction quotes and accessing all features of The Proper Tool.
      </p>

      <div className="w-full max-w-sm mb-8 py-5 border-t border-b">
        {codeApplied ? (
          <div className="flex items-center justify-center gap-2 text-sm text-primary bg-primary/10 border border-primary/30 rounded-lg px-4 py-3" data-testid="banner-referral-applied">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>Referral code applied — your referrer will earn a reward when you subscribe.</span>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm font-semibold text-foreground">Have a referral code? Enter it before subscribing.</p>
            <div className="flex gap-2 w-full">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="e.g. smith-builds-1234"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && referralCode.trim() && applyCodeMutation.mutate(referralCode)}
                  className="pl-9"
                  disabled={applyCodeMutation.isPending}
                  data-testid="input-referral-code-paywall"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => applyCodeMutation.mutate(referralCode)}
                disabled={!referralCode.trim() || applyCodeMutation.isPending}
                data-testid="button-apply-referral-paywall"
              >
                {applyCodeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {plansLoading ? (
        <div className="flex gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-72 w-56 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
          {plans?.map((plan: any) => {
            const isPro = plan.tier === "pro";
            const features = PLAN_FEATURES[plan.tier] ?? [];
            const description = PLAN_DESCRIPTIONS[plan.tier] ?? plan.description;
            return (
              <Card
                key={plan.id}
                className={`relative text-left ${isPro ? "border-primary border-2 shadow-lg" : ""}`}
                data-testid={`paywall-plan-card-${plan.tier}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground font-semibold">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pt-8 pb-8">
                  <h3 className="text-lg font-semibold mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">£{(plan.amount / 100).toFixed(0)}</span>
                    <span className="text-muted-foreground">/month + VAT</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">{description}</p>
                  <ul className="space-y-3 mb-8 text-sm">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isPro ? "default" : "outline"}
                    className={`w-full ${isPro ? "bg-primary text-primary-foreground font-semibold" : ""}`}
                    onClick={() => handleSubscribe(plan.tier)}
                    disabled={checkoutLoading === plan.tier}
                    data-testid={`paywall-button-subscribe-${plan.tier}`}
                  >
                    {checkoutLoading === plan.tier ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe Now"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const hasActiveSubscription = user?.subscriptionStatus === "active";
  const trialActive = !!(user?.trialEndAt && new Date(user.trialEndAt) > new Date());
  const needsAdminCheck = !hasActiveSubscription && !trialActive;

  const { data: adminData, isLoading: adminLoading } = useQuery<{ isAdmin: boolean }>({
    queryKey: ["/api/admin/check"],
    retry: false,
    enabled: needsAdminCheck,
  });

  if (!user) return <>{children}</>;
  if (hasActiveSubscription || trialActive) return <>{children}</>;
  if (needsAdminCheck && adminLoading) return null;
  if (adminData?.isAdmin === true) return <>{children}</>;

  return <TrialExpiredPaywall />;
}

function MenuToggleButton() {
  const { toggleSidebar } = useSidebar();
  return (
    <Button variant="outline" size="sm" onClick={toggleSidebar} data-testid="button-sidebar-toggle" className="gap-2">
      <Menu className="h-4 w-4" />
      <span>Menu</span>
    </Button>
  );
}

function QuotaLimitBanner() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  if (!user) return null;
  if (user.subscriptionStatus === "active") return null;

  const trialExpired = !!(user.trialEndAt && new Date(user.trialEndAt) <= new Date());
  const quotaHit = !!(user as any).trialQuotaExceeded || !!(user as any).quotaLimitEnforced;

  if (!trialExpired && !quotaHit) return null;

  const message = quotaHit
    ? "You've reached the shared free-trial allowance of 5 total quote creations across Auto, Template, and Manual quotes. Upgrade to keep creating quotes."
    : "Your free trial has expired after the shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. Upgrade to keep creating quotes.";

  return (
    <div className="flex items-center gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2.5 dark:bg-amber-950/30 dark:border-amber-800" data-testid="banner-quota-limit-enforced">
      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
      <p className="flex-1 text-sm text-amber-800 dark:text-amber-200">
        {message}
      </p>
      <Button
        size="sm"
        variant="default"
        className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white border-amber-700 focus-visible:ring-amber-700"
        onClick={() => navigate("/settings")}
        data-testid="button-quota-limit-upgrade"
      >
        Upgrade Now
      </Button>
    </div>
  );
}

function MobileAppBanner() {
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem("tpt_mobile_banner_dismissed") === "1"
  );
  if (dismissed) return null;
  return (
    <div className="sm:hidden flex items-start gap-3 bg-primary/10 border-b border-primary/20 px-4 py-2.5">
      <Monitor className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <p className="flex-1 text-xs text-foreground leading-snug">
        This app works best on a desktop or laptop. For the easiest experience, we strongly recommend switching to a larger screen.
      </p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => { localStorage.setItem("tpt_mobile_banner_dismissed", "1"); setDismissed(true); }}
        className="shrink-0 mt-0.5 text-muted-foreground hover:text-foreground"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <TourProvider>
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-full w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between h-14 px-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <MenuToggleButton />
            <div className="flex items-center gap-0.5">
              <TourButton />
              <HelpAgent />
              <FeedbackWidget />
              <ThemeToggle />
            </div>
          </header>
          <MobileAppBanner />
          <QuotaLimitBanner />
          <PendingQuoteNotice />
          <main className="flex-1 overflow-auto">
            <SubscriptionGate>
              {children}
            </SubscriptionGate>
          </main>
        </div>
      </div>
    </SidebarProvider>
    </TourProvider>
  );
}

function AuthenticatedRoutes() {
  return (
    <AuthenticatedLayout>
      <Switch>
        <Route path="/signup"><Redirect to="/auto-quote" /></Route>
        <Route path="/" component={DashboardPage} />
        <Route path="/dashboard" component={DashboardPage} />
        <Route path="/quotes" component={QuotesPage} />
        <Route path="/quotes/new" component={QuoteAIPage} />
        <Route path="/quotes/template" component={QuoteTemplatePage} />
        <Route path="/quotes/manual" component={QuoteManualPage} />
        <Route path="/quotes/survey" component={QuoteSurveyPage} />
        <Route path="/quotes/survey/:id" component={QuoteSurveyPage} />
        <Route path="/site-surveys" component={SiteSurveysPage} />
        <Route path="/quotes/:id/edit" component={QuoteEditPage} />
        <Route path="/quotes/:id" component={QuoteDetailPage} />
        <Route path="/clients" component={ClientsPage} />
        <Route path="/cost-library" component={CostLibraryPage} />
        <Route path="/adjustments" component={AdjustmentsPage} />
        <Route path="/pricing-intelligence" component={PricingIntelligencePage} />
        <Route path="/referrals" component={ReferralsPage} />
        <Route path="/shared-quotes">{() => { window.location.replace("/quotes"); return null; }}</Route>
        <Route path="/admin/withdrawals" component={AdminWithdrawalsPage} />
        <Route path="/admin/users" component={AdminUsersPage} />
        <Route path="/admin/theme" component={AdminThemePage} />
        <Route path="/admin/inbox" component={AdminInboxPage} />
        <Route path="/admin/maintenance" component={AdminMaintenancePage} />
        <Route path="/admin/partner-referrals" component={AdminPartnerReferralsPage} />
        <Route path="/admin/account-deletions" component={AdminAccountDeletionsPage} />
        <Route path="/admin/generated-quotes" component={AdminGeneratedQuotesPage} />
        <Route path="/admin/vocabulary-gaps" component={AdminVocabularyGapsPage} />
        <Route path="/admin/cost-library" component={AdminCostLibraryPage} />
        <Route path="/admin/recipe-drafts" component={AdminPriceReviewPage} />
        <Route path="/admin/price-review" component={AdminPriceReviewPage} />
        <Route path="/admin/quotes" component={AdminQuotesPage} />
        <Route path="/admin/messages" component={AdminUpdatesPage} />
        <Route path="/admin/updates" component={AdminUpdatesPage} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/quote-document-design" component={QuoteDocumentDesignPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/cookies" component={CookiesPage} />
        <Route path="/referral-terms" component={ReferralTermsPage} />
        <Route path="/blog" component={BlogIndexPage} />
        <Route path="/blog/:slug" component={BlogPostPage} />
        <Route path="/faq" component={FaqIndexPage} />
        <Route path="/faq/:slug" component={FaqPage} />
        <Route path="/comparisons" component={ComparisonsIndexPage} />
        <Route path="/alternatives/:slug" component={AlternativePage} />
        <Route path="/thank-you" component={ThankYouPage} />
        <Route path="/quoting-software-for-builders" component={QuotingSoftwareForBuildersPage} />
        <Route path="/construction-estimating-software" component={ConstructionEstimatingSoftwarePage} />
        <Route path="/best-quoting-software">{() => { window.location.replace("/best/quoting-software-for-uk-builders"); return null; }}</Route>
        <Route path="/best/quoting-software-for-uk-builders" component={BestQuotingSoftwareForUKBuildersPage} />
        <Route path="/buildxact-alternative" component={BuildxactAlternativePage} />
        <Route path="/estimatorxpress-alternative" component={EstimatorXpressAlternativePage} />
        <Route path="/simpro-alternative" component={SimproAlternativePage} />
        <Route path="/quoting-app-for-builders" component={QuotingAppForBuildersPage} />
        <Route path="/quoting-software-for-contractors" component={QuotingSoftwareForContractorsPage} />
        <Route path="/tradesman-quoting-tool" component={TradesmanQuotingToolPage} />
        <Route path="/quoting-software-for-electricians" component={ElectriciansPage} />
        <Route path="/quoting-software-for-plumbers" component={PlumbersPage} />
        <Route path="/quoting-software-for-roofers" component={RoofersPage} />
        <Route path="/quoting-software-for-carpenters" component={CarpentersPage} />
        <Route path="/quoting-software-for-plasterers" component={PlasterersPage} />
        <Route path="/quoting-software-for-decorators" component={DecoratorsPage} />
        <Route path="/job-quoting-software" component={JobQuotingSoftwarePage} />
        <Route path="/estimating-software-for-builders" component={EstimatingSoftwareForBuildersPage} />
        <Route path="/tradify-alternative" component={TradifyAlternativePage} />
        <Route path="/fergus-alternative" component={FergusAlternativePage} />
        <Route path="/estimage-alternative" component={EstimageAlternativePage} />
        <Route path="/pricejob-alternative" component={PricejobAlternativePage} />
        <Route path="/buildern-alternative" component={BuildernAlternativePage} />
        <Route path="/cubit-alternative" component={CubitAlternativePage} />
        <Route path="/evaluate-alternative" component={EvaluateAlternativePage} />
        <Route path="/quintadena-alternative" component={QuintadenaAlternativePage} />
        <Route path="/easyestimate-alternative" component={EasyestimateAlternativePage} />
        <Route path="/ai-quoting-software" component={AiQuotingSoftwarePage} />
        <Route path="/ai-quoting-software-uk" component={AiQuotingSoftwarePage} />
        <Route path="/auto-quote" component={QuoteWelcomePage} />
        <Route path="/ai-takeoff-software" component={AiTakeoffSoftwarePage} />
        <Route path="/quote-from-photo" component={QuoteFromPhotoPage} />
        <Route path="/quote-from-drawings" component={QuoteFromDrawingsPage} />
        <Route path="/quote-from-description" component={QuoteFromDescriptionPage} />
        <Route path="/ai-construction-estimating-software" component={AiConstructionEstimatingSoftwarePage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/for-small-builders" component={ForSmallBuildersPage} />
        <Route path="/for-sole-traders" component={ForSoleTradersPage} />
        <Route path="/guides" component={GuidesIndexPage} />
        <Route path="/guides/" component={GuidesIndexPage} />
        <Route path="/guides/quoting-for-uk-builders" component={GuideQuotingForUKBuildersPage} />
        <Route path="/guides/quoting-for-uk-builders/" component={GuideQuotingForUKBuildersPage} />
        <Route path="/guides/running-a-building-business-uk" component={GuideRunningABuildingBusinessUKPage} />
        <Route path="/guides/running-a-building-business-uk/" component={GuideRunningABuildingBusinessUKPage} />
        <Route path="/glossary" component={GlossaryPage} />
        <Route path="/uk-builder-quoting-survey-2026" component={UKBuilderQuotingSurvey2026Page} />
        {/* 301-equivalent client-side redirects for old UK-suffixed landing page slugs */}
        <Route path="/quoting-software-for-builders-uk">{() => { window.location.replace("/quoting-software-for-builders"); return null; }}</Route>
        <Route path="/construction-estimating-software-uk">{() => { window.location.replace("/construction-estimating-software"); return null; }}</Route>
        <Route path="/best-quoting-software-uk">{() => { window.location.replace("/best-quoting-software"); return null; }}</Route>
        <Route path="/quoting-app-for-builders-uk">{() => { window.location.replace("/quoting-app-for-builders"); return null; }}</Route>
        {/* Slug catch-all — handles referral links visited while logged in */}
        <Route path="/:slug" component={SlugLandingPage} />
        <Route component={NotFound} />
      </Switch>
    </AuthenticatedLayout>
  );
}

function ReferralWelcomeBanner() {
  const [company, setCompany] = useState<string>(() => localStorage.getItem("tpt_referrer_company") ?? "");
  const [dismissed, setDismissed] = useState<boolean>(() => sessionStorage.getItem("tpt_banner_dismissed") === "1");

  useEffect(() => {
    // Pick up company name after SlugLandingPage has set it and redirected back
    const check = () => {
      const c = localStorage.getItem("tpt_referrer_company") ?? "";
      setCompany(c);
    };
    check();
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, []);

  if (!company || dismissed) return null;

  return (
    <div className="bg-primary text-primary-foreground px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="banner-referral-welcome">
      <p className="text-sm flex-1 min-w-0">
        Welcome! You've been referred by <strong>{company}</strong> — sign up to claim your discount.
      </p>
      <button
        type="button"
        aria-label="Dismiss"
        className="shrink-0 opacity-80 hover:opacity-100 transition-opacity text-lg leading-none"
        onClick={() => {
          sessionStorage.setItem("tpt_banner_dismissed", "1");
          setDismissed(true);
        }}
        data-testid="button-dismiss-referral-banner"
      >
        &times;
      </button>
    </div>
  );
}

function ReferralAutoApply() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem("tpt_referral_code");
    if (!stored) return;
    localStorage.removeItem("tpt_referral_code");
    localStorage.removeItem("tpt_referrer_company");
    fetch("/api/referrals/apply", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ code: stored }),
    }).catch(() => {});
  }, [user]);

  return null;
}

function PendingTierRedirect() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const handled = useRef(false);

  useEffect(() => {
    if (!user || handled.current) return;
    const tier = localStorage.getItem("tpt_pending_tier");
    if (!tier) return;
    handled.current = true;
    localStorage.removeItem("tpt_pending_tier");
    navigate("/settings");
  }, [user]);

  return null;
}

function fireLeadConversion(eventID?: string) {
  try { (window as any).gtag("event", "conversion", { send_to: "AW-18031966412/UdfpCJHNj40cEMzxp5ZD" }); } catch (_) {}
  try {
    if (eventID) {
      (window as any).fbq("track", "Lead", {}, { eventID });
    } else {
      (window as any).fbq("track", "Lead");
    }
  } catch (_) {}
}

function NewSignupConversionTracker() {
  const { user } = useAuth();

  // Path 1: URL param set by server after email or Google OAuth redirect.
  // NOTE: on a full page reload (Google OAuth), the inline <head> script in
  // index.html fires the Google Ads conversion and strips "new_signup" from
  // the URL before React mounts (needed for Ads reliability), but it leaves
  // "fb_event_id" in place. So this must trigger on "fb_event_id" presence
  // too, not just "new_signup === '1'" — otherwise the OAuth path never
  // fires the eventID-tagged Meta Pixel Lead event and dedup breaks.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasNewSignupFlag = params.get("new_signup") === "1";
    const fbEventId = params.get("fb_event_id") || undefined;
    if (!hasNewSignupFlag && !fbEventId) return;
    params.delete("new_signup");
    params.delete("fb_event_id");
    const newUrl = [window.location.pathname, params.toString() ? "?" + params.toString() : ""].join("");
    window.history.replaceState({}, "", newUrl);
    fireLeadConversion(fbEventId);
    if (user) {
      localStorage.setItem(`tpt_lead_fired_${user.id}`, "1");
    }
  }, []);

  // Path 2: createdAt-based fallback — catches Google OAuth when the redirect
  // param is lost. Fires once per user, guarded by localStorage. Skipped if
  // Path 1 already fired for this user (it sets the same localStorage key).
  useEffect(() => {
    if (!user) return;
    const key = `tpt_lead_fired_${user.id}`;
    if (localStorage.getItem(key)) return;
    const ageMs = Date.now() - new Date((user as any).createdAt).getTime();
    if (ageMs > 5 * 60 * 1000) return; // account older than 5 min — not a new signup
    localStorage.setItem(key, "1");
    fireLeadConversion();
  }, [user]);

  return null;
}

function PresencePing() {
  const { user } = useAuth();
  const [location] = useLocation();
  useEffect(() => {
    if (!user) return;
    const ping = () =>
      fetch("/api/ping", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: location }),
      }).catch(() => {});
    ping();
    const id = setInterval(ping, 30_000);
    return () => clearInterval(id);
  }, [user, location]);
  return null;
}

export function trackEvent(eventType: string, page?: string, eventData?: Record<string, unknown>) {
  fetch("/api/track", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ eventType, page: page ?? window.location.pathname, eventData }),
  }).catch(() => {});
}

const LEGAL_PATHS = ["/terms", "/privacy", "/cookies", "/referral-terms"];

function TermsGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [location] = useLocation();
  const [accepted, setAccepted] = useState(false);
  const [market, setMarket] = useState<string>("uk");
  const [submitting, setSubmitting] = useState(false);

  if (!user || (user.termsAcceptedAt && user.market)) return <>{children}</>;
  if (LEGAL_PATHS.includes(location)) return <>{children}</>;

  const handleAccept = async () => {
    if (!accepted) return;
    setSubmitting(true);
    try {
      // Update market first
      await apiRequest("PATCH", "/api/me", { market });
      const res = await apiRequest("POST", "/api/auth/accept-terms");
      const updatedUser = await res.json();
      qc.setQueryData(["/api/auth/user"], updatedUser);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold">The Proper Tool</span>
        </div>
        <Card>
          <CardContent className="pt-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold mb-1">Before you get started</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Please select your operating region and accept our terms.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Where do you operate?
              </label>
              <Select value={market} onValueChange={setMarket}>
                <SelectTrigger data-testid="select-market-onboarding">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="uk">United Kingdom 🇬🇧</SelectItem>
                  <SelectItem value="us">United States 🇺🇸</SelectItem>
                  <SelectItem value="ca">Canada 🇨🇦</SelectItem>
                  <SelectItem value="au">Australia 🇦🇺</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
              <a href="/terms" target="_blank" className="flex items-center justify-between hover:text-primary transition-colors group">
                <span>Terms and Conditions</span>
                <span className="text-xs text-muted-foreground group-hover:text-primary">Read &rarr;</span>
              </a>
              <div className="border-t" />
              <a href="/privacy" target="_blank" className="flex items-center justify-between hover:text-primary transition-colors group">
                <span>Privacy Policy</span>
                <span className="text-xs text-muted-foreground group-hover:text-primary">Read &rarr;</span>
              </a>
              <div className="border-t" />
              <a href="/cookies" target="_blank" className="flex items-center justify-between hover:text-primary transition-colors group">
                <span>Cookie Policy</span>
                <span className="text-xs text-muted-foreground group-hover:text-primary">Read &rarr;</span>
              </a>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms-gate-accept"
                checked={accepted}
                onCheckedChange={(v) => setAccepted(!!v)}
                data-testid="checkbox-terms-gate"
              />
              <label htmlFor="terms-gate-accept" className="text-sm leading-relaxed cursor-pointer">
                I have read and agree to the Terms and Conditions, Privacy Policy, and Cookie Policy.
              </label>
            </div>
            <Button
              className="w-full"
              disabled={!accepted || submitting}
              onClick={handleAccept}
              data-testid="button-accept-terms"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept and continue"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ThemeLoader() {
  const { data: theme } = useQuery<{ light: Record<string, string>; dark: Record<string, string> } | null>({
    queryKey: ["/api/theme"],
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!theme) return;
    let styleEl = document.getElementById("custom-theme") as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "custom-theme";
      document.head.appendChild(styleEl);
    }
    const lightVars = Object.entries(theme.light).map(([k, v]) => `  --${k}: ${v};`).join("\n");
    const darkVars = Object.entries(theme.dark).map(([k, v]) => `  --${k}: ${v};`).join("\n");
    styleEl.textContent = `:root {\n${lightVars}\n}\n.dark {\n${darkVars}\n}`;
  }, [theme]);

  return null;
}

function MaintenancePage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="text-center max-w-md space-y-6">
        <div className="flex justify-center">
          <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
            <Wrench className="h-10 w-10 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">We'll be back shortly</h1>
          <p className="text-muted-foreground">
            The Proper Tool is currently undergoing scheduled maintenance. We're working hard to improve your experience and will be back online very soon.
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
          If you have an urgent enquiry, please email us at{" "}
          <a href="mailto:hello@thepropertool.com" className="text-primary underline underline-offset-4">
            hello@thepropertool.com
          </a>
        </div>
      </div>
    </div>
  );
}

function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const { data: maintenance } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/maintenance"],
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const { data: adminData } = useQuery<{ isAdmin: boolean }>({
    queryKey: ["/api/admin/check"],
    retry: false,
    enabled: maintenance?.enabled === true && !!user,
  });

  // Not in maintenance — show everything normally
  if (!maintenance?.enabled) return <>{children}</>;

  // Not logged in — always allow through so login page is accessible
  if (!user) return <>{children}</>;

  // Logged-in admin bypasses maintenance
  if (adminData?.isAdmin) return <>{children}</>;

  // Authenticated non-admin sees the maintenance page
  return <MaintenancePage />;
}

function AppContent() {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    };
    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, []);

  // A recovery link must be usable even when a different account is already
  // signed in, and must never be blocked by the subscription gate.
  if (location === "/reset-password") return <ResetPasswordPage />;

  // Public quote view — no auth required, customer accesses via emailed link
  if (location.startsWith("/q/")) {
    return (
      <Switch>
        <Route path="/q/:token" component={PublicQuotePage} />
      </Switch>
    );
  }

  if (location === "/try-a-quote" && !isLoading) {
    window.location.replace(getTryAQuoteDestination(Boolean(user)));
    return null;
  }

  // Keep the legacy claim continuation available for already-generated guest
  // quotes, but new visitors must register before starting a test quote.
  if (location === "/try-a-quote/claim") {
    return (
      <Switch>
        <Route path="/try-a-quote/claim" component={PublicGuestClaimPage} />
      </Switch>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <ReferralWelcomeBanner />
        <Switch>
          <Route path="/signup" component={SignupPage} />
          <Route path="/login" component={LoginPage} />
          <Route path="/structspectrum" component={StructSpectrumPage} />
          <Route path="/terms" component={TermsPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/cookies" component={CookiesPage} />
          <Route path="/referral-terms" component={ReferralTermsPage} />
          <Route path="/blog" component={BlogIndexPage} />
          <Route path="/blog/:slug" component={BlogPostPage} />
          <Route path="/faq" component={FaqIndexPage} />
          <Route path="/faq/:slug" component={FaqPage} />
          <Route path="/comparisons" component={ComparisonsIndexPage} />
          <Route path="/alternatives/:slug" component={AlternativePage} />
          <Route path="/thank-you" component={ThankYouPage} />
          <Route path="/quoting-software-for-builders" component={QuotingSoftwareForBuildersPage} />
          <Route path="/construction-estimating-software" component={ConstructionEstimatingSoftwarePage} />
          <Route path="/best-quoting-software">{() => { window.location.replace("/best/quoting-software-for-uk-builders"); return null; }}</Route>
          <Route path="/best/quoting-software-for-uk-builders" component={BestQuotingSoftwareForUKBuildersPage} />
          <Route path="/buildxact-alternative" component={BuildxactAlternativePage} />
          <Route path="/estimatorxpress-alternative" component={EstimatorXpressAlternativePage} />
          <Route path="/simpro-alternative" component={SimproAlternativePage} />
          <Route path="/quoting-app-for-builders" component={QuotingAppForBuildersPage} />
          <Route path="/quoting-software-for-contractors" component={QuotingSoftwareForContractorsPage} />
          <Route path="/tradesman-quoting-tool" component={TradesmanQuotingToolPage} />
          <Route path="/quoting-software-for-electricians" component={ElectriciansPage} />
          <Route path="/quoting-software-for-plumbers" component={PlumbersPage} />
          <Route path="/quoting-software-for-roofers" component={RoofersPage} />
          <Route path="/quoting-software-for-carpenters" component={CarpentersPage} />
          <Route path="/quoting-software-for-plasterers" component={PlasterersPage} />
          <Route path="/quoting-software-for-decorators" component={DecoratorsPage} />
          <Route path="/job-quoting-software" component={JobQuotingSoftwarePage} />
          <Route path="/estimating-software-for-builders" component={EstimatingSoftwareForBuildersPage} />
          <Route path="/tradify-alternative" component={TradifyAlternativePage} />
          <Route path="/fergus-alternative" component={FergusAlternativePage} />
          <Route path="/estimage-alternative" component={EstimageAlternativePage} />
          <Route path="/pricejob-alternative" component={PricejobAlternativePage} />
          <Route path="/buildern-alternative" component={BuildernAlternativePage} />
          <Route path="/cubit-alternative" component={CubitAlternativePage} />
          <Route path="/evaluate-alternative" component={EvaluateAlternativePage} />
          <Route path="/quintadena-alternative" component={QuintadenaAlternativePage} />
          <Route path="/easyestimate-alternative" component={EasyestimateAlternativePage} />
          <Route path="/ai-quoting-software" component={AiQuotingSoftwarePage} />
          <Route path="/ai-quoting-software-uk" component={AiQuotingSoftwarePage} />
          <Route path="/auto-quote" component={AutoQuotePage} />
          <Route path="/ai-takeoff-software" component={AiTakeoffSoftwarePage} />
          <Route path="/quote-from-photo" component={QuoteFromPhotoPage} />
          <Route path="/quote-from-drawings" component={QuoteFromDrawingsPage} />
          <Route path="/quote-from-description" component={QuoteFromDescriptionPage} />
          <Route path="/ai-construction-estimating-software" component={AiConstructionEstimatingSoftwarePage} />
          <Route path="/about" component={AboutPage} />
          <Route path="/for-small-builders" component={ForSmallBuildersPage} />
          <Route path="/for-sole-traders" component={ForSoleTradersPage} />
          <Route path="/guides" component={GuidesIndexPage} />
          <Route path="/guides/" component={GuidesIndexPage} />
          <Route path="/guides/quoting-for-uk-builders" component={GuideQuotingForUKBuildersPage} />
          <Route path="/guides/quoting-for-uk-builders/" component={GuideQuotingForUKBuildersPage} />
          <Route path="/guides/running-a-building-business-uk" component={GuideRunningABuildingBusinessUKPage} />
          <Route path="/guides/running-a-building-business-uk/" component={GuideRunningABuildingBusinessUKPage} />
          <Route path="/glossary" component={GlossaryPage} />
          <Route path="/uk-builder-quoting-survey-2026" component={UKBuilderQuotingSurvey2026Page} />
          {/* 301-equivalent client-side redirects for old UK-suffixed landing page slugs */}
          <Route path="/quoting-software-for-builders-uk">{() => { window.location.replace("/quoting-software-for-builders"); return null; }}</Route>
          <Route path="/construction-estimating-software-uk">{() => { window.location.replace("/construction-estimating-software"); return null; }}</Route>
          <Route path="/best-quoting-software-uk">{() => { window.location.replace("/best/quoting-software-for-uk-builders"); return null; }}</Route>
          <Route path="/quoting-app-for-builders-uk">{() => { window.location.replace("/quoting-app-for-builders"); return null; }}</Route>
          {/* Catch-all slug route — must come before the LandingPage catch-all */}
          <Route path="/:slug" component={SlugLandingPage} />
          <Route component={LandingPage} />
        </Switch>
      </>
    );
  }

  return (
    <TermsGate>
      <NewSignupConversionTracker />
      <ReferralAutoApply />
      <PendingTierRedirect />
      <PresencePing />
      <AuthenticatedRoutes />
    </TermsGate>
  );
}

function App() {
  return (
    <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <ThemeLoader />
            <MaintenanceGate>
              <AppContent />
            </MaintenanceGate>
            <Toaster />
            <PdfTemplateWarningListener />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </WouterRouter>
  );
}

export default App;
